/**
 * 재생 실적 보고서 — 윗선·광고주에 올리는 A4 인쇄물(PDF 로 저장해도 된다).
 *
 * 화면의 리포트 표는 축 하나씩 본다. 보고서는 한 장에 요약과 축 몇 개를 함께 싣는다 — 받는 사람은 콘솔을
 * 열지 않는다. 인쇄하면 사이드바·조작 단추만 빠진다(`styles.css` 의 `@media print`, 성적서와 같은 틀).
 */
import type { ReactNode } from 'react';
import type { ProofOfPlayReport, ProofOfPlayRow } from '../api/client.js';
import type { BrandLook } from '../components/brand.js';
import { formatSeconds } from '../components/ui.js';
import { printedAt } from '../inspection/snapshot.js';

/** 보고서에 싣는 축과 줄 수. 일자별은 기간 전체를 싣는다(최대 400일이지만 대개 한 달). */
export const SHEET_SECTIONS = [
  { axis: 'asset', title: '콘텐츠별', head: '콘텐츠', top: 20 },
  { axis: 'campaign', title: '캠페인별', head: '캠페인', top: 20 },
  { axis: 'location', title: '위치별', head: '위치', top: 20 },
  { axis: 'day', title: '일자별', head: '날짜', top: Infinity },
] as const;

export interface SheetData {
  totals: ProofOfPlayReport['totals'];
  sections: Record<(typeof SHEET_SECTIONS)[number]['axis'], ProofOfPlayRow[]>;
}

export function ReportSheet({
  data,
  from,
  to,
  tz,
  brand,
  now = new Date(),
}: {
  data: SheetData;
  /** 보는 사람이 고른 날짜(YYYY-MM-DD). */
  from: string;
  to: string;
  tz: string;
  brand: BrandLook;
  now?: Date;
}): ReactNode {
  const { totals } = data;
  return (
    <article className="sheet report-sheet">
      <header className="sheet-head">
        <div>
          <h1>재생 실적 보고서</h1>
          <p>
            {from} ~ {to} · {tz}
          </p>
        </div>
        <div className="report-sheet-brand">
          {brand.logoUrl ? <img src={brand.logoUrl} alt="" /> : null}
          {brand.name ? <span>{brand.name}</span> : null}
        </div>
      </header>

      <div className="report-sheet-totals">
        <Total label="총 재생 횟수" value={`${totals.plays.toLocaleString('ko-KR')}회`} />
        <Total label="총 노출 시간" value={formatSeconds(totals.seconds)} />
        <Total label="재생한 화면" value={`${totals.displays.toLocaleString('ko-KR')}대`} />
        <Total label="재생된 콘텐츠" value={`${totals.assets.toLocaleString('ko-KR')}개`} />
      </div>

      {SHEET_SECTIONS.map((section) => {
        const rows = data.sections[section.axis];
        const shown = Number.isFinite(section.top) ? rows.slice(0, section.top) : rows;
        return (
          <section key={section.axis} className="sheet-block">
            <h2>
              {section.title}
              {rows.length > shown.length ? <span>상위 {shown.length}개 · 전체 {rows.length}개</span> : null}
              {section.axis === 'campaign' ? <span>캠페인 기간 안에 그 캠페인의 플레이리스트로 나온 재생</span> : null}
            </h2>
            {shown.length === 0 ? (
              <p className="report-sheet-empty">이 기간에 해당하는 재생이 없습니다.</p>
            ) : (
              <table className="sheet-table report-sheet-table">
                <thead>
                  <tr>
                    <th>{section.head}</th>
                    <th className="col-num">재생 횟수</th>
                    <th className="col-num">노출 시간</th>
                    <th className="col-num">화면 수</th>
                    <th className="col-num">비중</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr key={row.key || row.label}>
                      <td>{row.label}</td>
                      <td className="col-num">{row.plays.toLocaleString('ko-KR')}</td>
                      <td className="col-num">{formatSeconds(row.seconds)}</td>
                      <td className="col-num">{row.displays.toLocaleString('ko-KR')}</td>
                      <td className="col-num">{share(row.plays, totals.plays)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}

      <footer className="report-sheet-foot">
        <span>SignageOne · 화면이 실제로 재생하고 보고한 기록을 집계했습니다.</span>
        <span>만든 시각 {printedAt(now)}</span>
      </footer>
    </article>
  );
}

function Total({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <div className="report-sheet-total-label">{label}</div>
      <div className="report-sheet-total-value">{value}</div>
    </div>
  );
}

/** 전체에서 차지하는 비율 — 소수 한 자리. 캠페인은 겹칠 수 있어 더하면 100% 를 넘을 수 있다. */
export function share(part: number, total: number): string {
  if (total <= 0) return '—';
  return `${((part / total) * 100).toFixed(1)}%`;
}
